<template>
  <div>
  <AppModal
    :open="modelValue"
    :title="$t('app.logoAndBanner')"
    :description="$t('app.logoBannerDescription')"
    test-id="public-owner-media-modal"
    @close="onClose"
  >
    <div class="space-y-6" data-testid="public-owner-media-modal-content">
      <p class="text-sm text-slate-600 dark:text-slate-400">{{ $t('app.uploadImagesHint') }}</p>
      <div data-testid="public-owner-media-modal-logo">
        <p class="text-sm font-medium text-charcoal dark:text-white mb-2">{{ $t('app.logo') }}</p>
        <div class="flex flex-col sm:flex-row gap-4 items-start">
          <div class="relative shrink-0">
            <div class="relative w-20 h-20 rounded-xl bg-slate-100 dark:bg-zinc-800 overflow-hidden flex items-center justify-center border border-slate-200 dark:border-zinc-700">
              <img v-if="logoUrl" :src="logoUrl" :key="logoUrl" alt="" class="w-full h-full object-contain" />
              <span v-else class="material-icons text-3xl text-slate-400">image</span>
              <div
                v-if="uploadingLogo || (removingMedia && removeMediaTarget === 'logo')"
                class="absolute inset-0 bg-slate-900/60 flex items-center justify-center rounded-xl"
                aria-busy="true"
                :aria-label="$t('app.ariaUploadingLogo')"
              >
                <span class="material-icons text-3xl text-white animate-spin">sync</span>
              </div>
            </div>
            <button
              v-if="logoUrl"
              type="button"
              class="absolute -top-2 -right-2 z-10 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow min-h-[14px] min-w-[14px]"
              :aria-label="$t('app.ariaRemoveLogoImage')"
              :disabled="uploadingLogo || removingMedia"
              data-testid="public-owner-media-logo-remove"
              @click="removeMediaTarget = 'logo'"
            >
              <span class="material-icons text-lg" aria-hidden="true">close</span>
            </button>
          </div>
          <div class="min-w-0 w-full sm:w-auto flex flex-col gap-2">
            <input
              ref="logoInputRef"
              type="file"
              :accept="ACCEPT_IMAGE"
              class="sr-only"
              :aria-label="$t('app.ariaUploadLogoFromGallery')"
              data-testid="public-owner-media-logo-input"
              @change="onModalLogoChange"
            />
            <input
              ref="logoCameraInputRef"
              type="file"
              :accept="ACCEPT_IMAGE"
              capture="environment"
              class="sr-only"
              :aria-label="$t('app.ariaTakePhotoLogo')"
              data-testid="public-owner-media-logo-camera-input"
              @change="onModalLogoChange"
            />
            <div class="flex flex-wrap gap-2 sm:block">
              <AppButton type="button" variant="secondary" size="sm" class="min-h-[44px] sm:hidden" :disabled="uploadingLogo || removingMedia" @click="triggerLogoCamera">{{ $t('app.takePhoto') }}</AppButton>
              <AppButton type="button" variant="secondary" size="sm" class="min-h-[44px] sm:hidden" :disabled="uploadingLogo || removingMedia" @click="triggerLogoInput">{{ $t('app.chooseFromGallery') }}</AppButton>
              <AppButton type="button" variant="secondary" size="sm" class="min-h-[44px] hidden sm:inline-flex" :disabled="uploadingLogo || removingMedia" @click="triggerLogoInput">{{ $t('app.changeLogo') }}</AppButton>
            </div>
            <p v-if="imageUploadErrors.logo" class="text-xs text-red-600 dark:text-red-400 mt-1" role="alert">{{ imageUploadErrors.logo }}</p>
          </div>
        </div>
      </div>
      <div data-testid="public-owner-media-modal-banner">
        <p class="text-sm font-medium text-charcoal dark:text-white mb-2">{{ $t('app.banner') }}</p>
        <div class="flex flex-col sm:flex-row gap-4 items-start">
          <div class="relative shrink-0">
            <div class="relative w-20 h-20 sm:w-auto sm:max-w-[200px] aspect-video rounded-xl bg-slate-100 dark:bg-zinc-800 overflow-hidden flex items-center justify-center border border-slate-200 dark:border-zinc-700">
              <img v-if="bannerUrl" :src="bannerUrl" :key="bannerUrl" alt="" class="w-full h-full object-cover" />
              <span v-else class="material-icons text-3xl text-slate-400">image</span>
              <div
                v-if="uploadingBanner || (removingMedia && removeMediaTarget === 'banner')"
                class="absolute inset-0 bg-slate-900/60 flex items-center justify-center rounded-xl"
                aria-busy="true"
                :aria-label="$t('app.ariaUploadingBanner')"
              >
                <span class="material-icons text-3xl text-white animate-spin">sync</span>
              </div>
            </div>
            <button
              v-if="bannerUrl"
              type="button"
              class="absolute -top-2 -right-2 z-10 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow min-h-[18px] min-w-[18px]"
              :aria-label="$t('app.ariaRemoveBannerImage')"
              :disabled="uploadingBanner || removingMedia"
              data-testid="public-owner-media-banner-remove"
              @click="removeMediaTarget = 'banner'"
            >
              <span class="material-icons text-lg" aria-hidden="true">close</span>
            </button>
          </div>
          <div class="min-w-0 w-full sm:w-auto flex flex-col gap-2">
            <input
              ref="bannerInputRef"
              type="file"
              :accept="ACCEPT_IMAGE"
              class="sr-only"
              :aria-label="$t('app.ariaUploadBannerFromGallery')"
              data-testid="public-owner-media-banner-input"
              @change="onModalBannerChange"
            />
            <input
              ref="bannerCameraInputRef"
              type="file"
              :accept="ACCEPT_IMAGE"
              capture="environment"
              class="sr-only"
              :aria-label="$t('app.ariaTakePhotoBanner')"
              data-testid="public-owner-media-banner-camera-input"
              @change="onModalBannerChange"
            />
            <div class="flex flex-wrap gap-2 sm:block">
              <AppButton type="button" variant="secondary" size="sm" class="min-h-[44px] sm:hidden" :disabled="uploadingBanner || removingMedia" @click="triggerBannerCamera">{{ $t('app.takePhoto') }}</AppButton>
              <AppButton type="button" variant="secondary" size="sm" class="min-h-[44px] sm:hidden" :disabled="uploadingBanner || removingMedia" @click="triggerBannerInput">{{ $t('app.chooseFromGallery') }}</AppButton>
              <AppButton type="button" variant="secondary" size="sm" class="min-h-[44px] hidden sm:inline-flex" :disabled="uploadingBanner || removingMedia" @click="triggerBannerInput">{{ $t('app.changeBanner') }}</AppButton>
            </div>
            <p v-if="imageUploadErrors.banner" class="text-xs text-red-600 dark:text-red-400 mt-1" role="alert">{{ imageUploadErrors.banner }}</p>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <AppButton variant="primary" data-testid="public-owner-media-modal-done" @click="onClose">{{ $t('app.done') }}</AppButton>
    </template>
  </AppModal>

  <ImageCropModal
    v-if="cropImageUrl"
    :open="!!cropImageUrl"
    :image-url="cropImageUrl"
    :aspect-ratio="cropMode === 'banner' ? 21 / 9 : undefined"
    :export-format="cropMode === 'logo' ? 'png' : 'jpeg'"
    :fileName="cropFileName"
    data-testid="public-owner-media-crop-modal"
    @confirm="onCropConfirm"
    @cancel="onCropCancel"
  />

  <AppModal
    :open="removeMediaTarget !== null"
    :title="removeMediaTarget === 'banner' ? $t('app.confirmRemoveBannerTitle') : $t('app.confirmRemoveLogoTitle')"
    :description="removeMediaTarget === 'banner' ? $t('app.confirmRemoveBannerMessage') : $t('app.confirmRemoveLogoMessage')"
    test-id="public-owner-remove-media-modal"
    :close-on-backdrop-click="!removingMedia"
    @close="removeMediaTarget = null"
  >
    <p class="text-sm text-slate-600 dark:text-slate-400">
      {{ removeMediaTarget === 'banner' ? $t('app.confirmRemoveBannerMessage') : $t('app.confirmRemoveLogoMessage') }}
    </p>
    <template #footer>
      <AppButton variant="secondary" class="min-h-[44px]" :disabled="removingMedia" @click="removeMediaTarget = null">
        {{ $t('app.cancel') }}
      </AppButton>
      <AppButton variant="primary" class="min-h-[44px] bg-red-600 hover:bg-red-700" :disabled="removingMedia" @click="executeRemoveLogoOrBanner">
        <template v-if="removingMedia" #icon><span class="material-icons animate-spin text-lg">sync</span></template>
        {{ removeMediaTarget === 'banner' ? $t('app.removeBanner') : $t('app.removeLogo') }}
      </AppButton>
    </template>
  </AppModal>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AppButton from '@/components/ui/AppButton.vue'
import AppModal from '@/components/ui/AppModal.vue'
import ImageCropModal from '@/components/ui/ImageCropModal.vue'
import Restaurant from '@/models/Restaurant.js'
import { useToastStore } from '@/stores/toast'
import { restaurantService, normalizeApiError } from '@/services'
import { prepareImageFile, compressImageToMaxBytes, ACCEPT_IMAGE, MAX_IMAGE_BYTES, MAX_BANNER_BYTES } from '@/utils/imageUpload'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  restaurantUuid: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  bannerUrl: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'updated'])

const { t } = useI18n()
const toastStore = useToastStore()

const imageUploadErrors = ref({ logo: '', banner: '' })
const uploadingLogo = ref(false)
const uploadingBanner = ref(false)
const removeMediaTarget = ref(null)
const removingMedia = ref(false)
const logoInputRef = ref(null)
const logoCameraInputRef = ref(null)
const bannerInputRef = ref(null)
const bannerCameraInputRef = ref(null)
const cropImageUrl = ref('')
const cropMode = ref('logo')
const cropFileName = ref('')

function onClose() {
  emit('update:modelValue', false)
  imageUploadErrors.value = { logo: '', banner: '' }
  removeMediaTarget.value = null
  clearCropState()
}

function triggerLogoInput() {
  imageUploadErrors.value.logo = ''
  logoInputRef.value?.click()
}
function triggerLogoCamera() {
  imageUploadErrors.value.logo = ''
  logoCameraInputRef.value?.click()
}
function triggerBannerInput() {
  imageUploadErrors.value.banner = ''
  bannerInputRef.value?.click()
}
function triggerBannerCamera() {
  imageUploadErrors.value.banner = ''
  bannerCameraInputRef.value?.click()
}

async function onModalLogoChange(ev) {
  const rawFile = ev.target?.files?.[0]
  if (!rawFile || !props.restaurantUuid) return
  imageUploadErrors.value.logo = ''
  try {
    const file = await prepareImageFile(rawFile)
    const url = URL.createObjectURL(file)
    cropImageUrl.value = url
    cropMode.value = 'logo'
    cropFileName.value = file.name ?? 'logo.jpg'
    if (logoInputRef.value) logoInputRef.value.value = ''
    if (logoCameraInputRef.value) logoCameraInputRef.value.value = ''
  } catch (e) {
    imageUploadErrors.value.logo = normalizeApiError(e).message ?? t('app.somethingWentWrongTryAgain')
  }
}

async function onModalBannerChange(ev) {
  const rawFile = ev.target?.files?.[0]
  if (!rawFile || !props.restaurantUuid) return
  imageUploadErrors.value.banner = ''
  try {
    const file = await prepareImageFile(rawFile)
    const url = URL.createObjectURL(file)
    cropImageUrl.value = url
    cropMode.value = 'banner'
    cropFileName.value = file.name ?? 'banner.jpg'
    if (bannerInputRef.value) bannerInputRef.value.value = ''
    if (bannerCameraInputRef.value) bannerCameraInputRef.value.value = ''
  } catch (e) {
    imageUploadErrors.value.banner = normalizeApiError(e).message ?? t('app.somethingWentWrongTryAgain')
  }
}

function clearCropState() {
  if (cropImageUrl.value) {
    URL.revokeObjectURL(cropImageUrl.value)
  }
  cropImageUrl.value = ''
  cropMode.value = 'logo'
  cropFileName.value = ''
}

async function onCropConfirm(croppedFile) {
  if (!croppedFile || !props.restaurantUuid) {
    clearCropState()
    return
  }
  const isLogo = cropMode.value === 'logo'
  if (isLogo) uploadingLogo.value = true
  else uploadingBanner.value = true
  imageUploadErrors.value.logo = ''
  imageUploadErrors.value.banner = ''
  const urlToRevoke = cropImageUrl.value
  cropImageUrl.value = ''
  cropMode.value = 'logo'
  cropFileName.value = ''
  if (urlToRevoke) URL.revokeObjectURL(urlToRevoke)
  try {
    if (isLogo) {
      const compressed = await compressImageToMaxBytes(croppedFile, MAX_IMAGE_BYTES)
      const res = await restaurantService.uploadLogo(props.restaurantUuid, compressed)
      if (res != null) {
        const payload = Restaurant.fromApi(res).toJSON()
        if (payload.logo_url) payload.logo_url = `${payload.logo_url}?t=${Date.now()}`
        emit('updated', payload)
      }
      toastStore.success(t('app.imageUpdatedToast'))
    } else {
      const compressed = await compressImageToMaxBytes(croppedFile, MAX_BANNER_BYTES)
      const res = await restaurantService.uploadBanner(props.restaurantUuid, compressed)
      if (res != null) {
        const payload = Restaurant.fromApi(res).toJSON()
        if (payload.banner_url) payload.banner_url = `${payload.banner_url}?t=${Date.now()}`
        emit('updated', payload)
      }
      toastStore.success(t('app.imageUpdatedToast'))
    }
    if (logoInputRef.value) logoInputRef.value.value = ''
    if (logoCameraInputRef.value) logoCameraInputRef.value.value = ''
    if (bannerInputRef.value) bannerInputRef.value.value = ''
    if (bannerCameraInputRef.value) bannerCameraInputRef.value.value = ''
  } catch (e) {
    if (isLogo) imageUploadErrors.value.logo = normalizeApiError(e).message ?? t('app.somethingWentWrongTryAgain')
    else imageUploadErrors.value.banner = normalizeApiError(e).message ?? t('app.somethingWentWrongTryAgain')
  } finally {
    uploadingLogo.value = false
    uploadingBanner.value = false
  }
}

function onCropCancel() {
  clearCropState()
}

async function executeRemoveLogoOrBanner() {
  const target = removeMediaTarget.value
  if (!target || !props.restaurantUuid) return
  removingMedia.value = true
  try {
    imageUploadErrors.value.logo = ''
    imageUploadErrors.value.banner = ''
    const res =
      target === 'logo'
        ? await restaurantService.deleteLogo(props.restaurantUuid)
        : await restaurantService.deleteBanner(props.restaurantUuid)
    if (res != null) {
      const payload = Restaurant.fromApi(res).toJSON()
      emit('updated', payload)
    }
    removeMediaTarget.value = null
    toastStore.success(target === 'logo' ? t('app.logoRemovedToast') : t('app.bannerRemovedToast'))
  } catch (e) {
    const msg = normalizeApiError(e).message ?? t('app.somethingWentWrongTryAgain')
    if (target === 'logo') imageUploadErrors.value.logo = msg
    else imageUploadErrors.value.banner = msg
  } finally {
    removingMedia.value = false
  }
}
</script>
